import { inject, Injectable } from '@angular/core';
import { GenericApiService } from '@core/generic-api/data-access';
import { SendOtpResponse } from '../type/send-otp.type';
import { interval, last, map, Observable, scan, switchMap, takeWhile, tap, throwError } from 'rxjs';
import { LoanOfferResponse } from '../type/loan-offer.type';
import { LoanQuoteResponse } from '../type/loan-quote.type';
import { BizflexFacade } from '../store';
import { ApplicationInfoResponse } from '../model/application-info.model';
import { SubmitLoanDto } from '../dto/submit-loan.dto';
import { CompanyModel } from '../model/company.model';
import { CheckLetterOfOfferResponse, LetterOfOfferSignMethod } from '../type/letter-of-offer.type';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DirectorModel } from '../model/director.model';
import { SKIP_RECAPTCHA_INTERCEPTOR } from '@core/recaptcha/data-access';
import { SKIP_ERROR_PAGE_INTERCEPTOR, SKIP_RETRY_INTERCEPTOR } from '@core/data-access';

@Injectable({ providedIn: 'root' })
export class ApplicationApiService {
  private readonly apiService = inject(GenericApiService);
  private readonly bizflexFacade = inject(BizflexFacade);

  sendOtp(dto: { appId: string; last4NRIC: string }): Observable<SendOtpResponse> {
    const headers = this.apiService.createHeaders().set(SKIP_RETRY_INTERCEPTOR, 'true').set(SKIP_ERROR_PAGE_INTERCEPTOR, 'true');

    return this.apiService.post(this.getEndpoint('otp'), dto, { requestOptions: { headers } }).pipe(
      map(res => {
        if (!res) throw new HttpErrorResponse({ status: 400, error: 'Invalid NRIC' });
        return { ...dto, maskPhone: res.maskPhone, otpType: res.otpType };
      }),
      tap(res => console.log(res)),
      // catchError(() => {
      //   return throwError(
      //     () =>
      //       new HttpErrorResponse({
      //         error: {
      //           statusCode: 400,
      //           message: 'Bad Request Exception',
      //           errorCode: 'BIZ011',
      //           error: {
      //             errorCode: 'BIZ011',
      //             reasons: 'Cannot submit application when case status is not CUSTOMER_PROCESSING',
      //           },
      //         },
      //       }),
      //   );
      // }),
    );
  }

  /**
   * @returns JWT token
   */
  verifyOtp(dto: { appId: string; last4NRIC: string; otpCode: string }): Observable<string> {
    const headers = this.apiService.createHeaders().set(SKIP_RETRY_INTERCEPTOR, 'true').set(SKIP_ERROR_PAGE_INTERCEPTOR, 'true');

    return this.apiService.post(this.getEndpoint('verify'), dto, { requestOptions: { headers } }).pipe(
      tap(() => this.bizflexFacade.setApplicationId(dto.appId)),
      tap(token => this.bizflexFacade.setAccessToken(token)),
    );
  }

  getApplicationInfo(): Observable<ApplicationInfoResponse> {
    return this.apiService.get(this.getEndpoint('information')).pipe(
      map(res => ApplicationInfoResponse.fromJson(res)),
      tap(res => {
        const company: InstanceType<typeof CompanyModel> = { name: res.companyName, uen: res.uen };
        const director: InstanceType<typeof DirectorModel> = {
          name: res.name,
          nric: res.last4NRIC,
          appointment: res.appointment,
          accountNumber: res.accountNumber,
        };

        this.bizflexFacade.setCurrencyCode(res.currencyCode);
        this.bizflexFacade.setCompany(CompanyModel.fromJson(company));
        this.bizflexFacade.setDirector(DirectorModel.fromJson(director));
        this.bizflexFacade.setDirectors(res.caseKeyPersonnels);
      }),
      tap(res => console.log(res)),
    );
  }

  checkLoanOffer(): Observable<LoanOfferResponse> {
    return this.apiService.get(this.getEndpoint('offer')).pipe(tap(res => console.log(res)));
  }

  checkCbs(): Observable<boolean> {
    const headers = this.apiService.createHeaders().set(SKIP_RETRY_INTERCEPTOR, 'true');
    return this.apiService
      .get(this.getEndpoint('validate-cbs'), undefined, { requestOptions: { headers } })
      .pipe(tap(res => console.log(res)));
  }

  pollApiCheckLetterOfOfferUntilAssigned(args: { attempt: number; interval: number }): Observable<CheckLetterOfOfferResponse> {
    const maxAttempts = args.attempt;

    return interval(args.interval).pipe(
      // Keep track of attempts and response
      scan(acc => ({ attempts: acc.attempts + 1, response: null }), { attempts: 0, response: null }),

      // Make API call for each interval
      switchMap(state => {
        if (state.attempts >= maxAttempts) return throwError(() => new Error('Maximum retry attempts reached'));
        return this.apiService.get(this.getEndpoint('letter-offer-status'));
      }),

      // Continue while status isn't 'lo-assigned' and attempts < max
      takeWhile((response, index) => response.status !== 'lo-assigned' && index < maxAttempts - 1, true),

      // Get the last emission (either success or last attempt before error)
      last(),

      tap(res => console.log(res)),
    );
  }

  getContract(signToken: string): Observable<Record<string, any>> {
    return this.apiService.get('contracts-participants/contract', null, {
      requestOptions: { headers: new HttpHeaders({ Authorization: `Bearer ${signToken}` }) },
    });
  }

  /**
   * @returns JWT token
   */
  getSignToken(): Observable<string> {
    return this.apiService.get<string>(this.getEndpoint('singpass')).pipe(tap(res => this.bizflexFacade.setSignToken(res)));
  }

  postLoanQuote(dto: { holdback: number; loanAmount: number }): Observable<LoanQuoteResponse> {
    const headers = this.apiService.createHeaders().set(SKIP_RECAPTCHA_INTERCEPTOR, 'true');
    return this.apiService.post(this.getEndpoint('quote'), dto, { requestOptions: { headers } }).pipe(tap(res => console.log(res)));
  }

  getLoanQuote(): Observable<LoanQuoteResponse> {
    return this.apiService.get(this.getEndpoint('quote')).pipe(tap(res => console.log(res)));
  }

  submitLoan(dto: SubmitLoanDto): Observable<{ message: string }> {
    return this.apiService.post(this.getEndpoint('submit'), dto).pipe(tap(res => console.log(res)));
  }

  submitByDirector(): Observable<{ message: string; signType?: LetterOfOfferSignMethod }> {
    return this.apiService.post(this.getEndpoint('submit-by-director')).pipe(tap(res => console.log(res)));
  }

  setSignMethods(dto: { signType: LetterOfOfferSignMethod }): Observable<{ signType: LetterOfOfferSignMethod }> {
    return this.apiService.post(this.getEndpoint('sign-methods'), dto).pipe(tap(res => console.log(res)));
  }

  private getEndpoint(...endpoint: string[]): string {
    return ['biz-flex-offers', ...endpoint].join('/');
  }
}
